import { useState } from 'react';
import { useStep } from '../Base/StepContext';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@material-tailwind/react';
import AuthenticationService from '../../Core/Services/AuthenticationService';

export default function AlreadyLoggedIn({ user, setUser }) {
  const { t } = useTranslation();
  const { handleNext } = useStep();

  // Check for missing fields
  const requiredFields = ['phone', 'firstName', 'lastName', 'email'];
  const requiredAddressFields = ['streetAddress', 'city', 'country', 'zipCode'];

  // Local state
  const [formValues, setFormValues] = useState({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Check for missing address fields in the address object
  let missingAddressFields = [];
  if (!user.address || typeof user.address !== 'object') {
    missingAddressFields = requiredAddressFields;
  } else {
    missingAddressFields = requiredAddressFields.filter(field => {
      const value = user.address[field];
      if (typeof value === 'string') {
        return value.trim() === '';
      }
      return value == null;
    });
  }

  // Check for missing non-address fields i.e phone, firstName, lastName, email
  const missingFields = requiredFields.filter(field => {
    const value = user?.[field];
    if (typeof value === 'string') {
      return value.trim() === '';
    }
    return value == null;
  });

  //Clear user data and token on logout
  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    setUser(null);
  };

  const handleChange = (key, value) => {
    setFormValues(prev => ({ ...prev, [key]: value }));
  };

  const handleAddressChange = (key, value) => {
    setFormValues(prev => ({
      ...prev,
      address: {
        ...(prev.address || {}),
        [key]: value,
      },
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    // Validate all missing fields
    for (const field of missingFields) {
      if (!formValues[field] || formValues[field].trim() === '') {
        setError('all_fields_required');
        return;
      }
    }
    for (const field of missingAddressFields) {
      if (
        !formValues.address ||
        !formValues.address[field] ||
        formValues.address[field].trim() === ''
      ) {
        setError('all_fields_required');
        return;
      }
    }
    setSubmitting(true);
    // Merge address into user object
    const userData = {
      ...user,
      ...formValues,
      address: {
        ...(user.address || {}),
        ...(formValues.address || {}),
      },
    };
    const result = await AuthenticationService.updateProfile(userData);
    if (result.success) {
      setUser(result.user);
      setSubmitting(false);
      handleNext();
    } else {
      setError(result.message || t('could_not_update_profile'));
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col items-center">
        {missingFields.length > 0 || missingAddressFields.length > 0 ? (
          <form className="w-full max-w-md mt-6" onSubmit={handleSubmit}>
            <h2 className="text-lg font-bold mb-2 text-center">
              {t('complete_your_profile')}
            </h2>
            {error && (
              <div className="bg-red-100 text-red-700 px-4 py-2 rounded mb-2 text-center animate-pulse">
                {error}
              </div>
            )}
            {missingFields.map(field => (
              <Input
                key={field}
                label={field.charAt(0).toUpperCase() + field.slice(1)}
                value={formValues[field] || ''}
                onChange={e => handleChange(field, e.target.value)}
                className="mb-4"
                disabled={submitting}
              />
            ))}
            {missingAddressFields.length > 0 && (
              <div className="mt-4">
                <h3 className="font-semibold mb-2">Adresse</h3>
                {missingAddressFields.map(field => (
                  <Input
                    key={field}
                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                    value={formValues.address?.[field] || ''}
                    onChange={e => handleAddressChange(field, e.target.value)}
                    className="mb-4"
                    disabled={submitting}
                  />
                ))}
              </div>
            )}
            <div
              onClick={handleSubmit}
              type="submit"
              className="bg-primaryblue text-white px-6 py-2 rounded-none shadow font-semibold text-base w-full mt-2 text-center cursor-pointer hover:bg-blue-700"
              disabled={submitting}
            >
              {t('continue')}
            </div>
          </form>
        ) : (
          <div
            className="mb-6 border-gray-300 !border !border-solid p-6 gap-2 w-[90%]"
            style={{ backgroundColor: '#F5F5F5' }}
          >
            <div className="text-base">
              {t('logged_in_as')}{' '}
              <span className="font-bold">{user.email}</span>.
            </div>

            <div className="flex justify-between mt-6 gap-12">
              <Button
                type="outline"
                onClick={handleLogout}
                className="bg-red-500 text-white px-6 py-2 rounded-none shadow font-semibold text-base"
                style={{
                  backgroundColor: 'rgb(220, 38, 38)',
                  border: 'none',
                }}
                onMouseOver={e =>
                  (e.currentTarget.style.backgroundColor = 'rgb(185, 28, 28)')
                }
                onMouseOut={e =>
                  (e.currentTarget.style.backgroundColor = 'rgb(220, 38, 38)')
                }
              >
                Log ud
              </Button>
              <Button
                onClick={handleNext}
                className="bg-primaryblue text-white px-6 py-2 rounded-none shadow font-semibold text-base"
              >
                {t('confirm_booking')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
